import { Button, Empty, Flex, Spin, Typography } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMachineDetailsRobotSchedule } from '../../context/MachineDetailsRobotScheduleContext';
import { MAP_GROUP_NAME_OTHERS } from '../../utils/map';
import { useMapAssignmentModalState } from '../../hooks/useMapAssignmentModal';
import {
  MapAssignmentCloseButton,
  MapAssignmentGroupColumn,
  MapAssignmentGroupRowRight,
  MapAssignmentListContainer,
  MapAssignmentListRow,
  MapAssignmentListRows,
  MapAssignmentListsWrapper,
  MapAssignmentMapImage,
  MapAssignmentMapItemRight,
  MapAssignmentModalContainer,
  MapAssignmentRowDivider,
  MapAssignmentRowLeft,
  MapAssignmentRowRight,
  MapAssignmentSearchContainer,
  MapAssignmentSearchInput,
  MapAssignmentSectionTitleText,
  MapAssignmentSelectionTag,
} from './MapAssignmentModal.styles';
import { KccCheckbox } from 'lib/components/KccCheckbox/KccCheckbox';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';
import mapPlaceHolder from 'lib/assets/images/Routine_Example.png';

export const MapAssignmentModal = (): JSX.Element => {
  const { t } = useTranslation();

  const { robot, isMapAssignmentModalOpen, handleMapAssignmentAssigned, handleCloseMapAssignmentModal } =
    useMachineDetailsRobotSchedule();

  const {
    searchTerm,
    selectedMapIds,
    selectedCount,
    mapGroups,
    isMapsLoading,
    isMapGroupsLoading,
    isAssigning,
    mapGroupsHeaderState,
    mapsHeaderState,
    handleSearchChange,
    handleToggleMap,
    handleToggleGroup,
    handleToggleAllMapGroups,
    handleToggleAllMaps,
    handleClearSelection,
    handleCancel,
    handleAssignAndContinue,
    getGroupCheckedState,
  } = useMapAssignmentModalState({
    robot,
    isOpen: isMapAssignmentModalOpen,
    onAssigned: handleMapAssignmentAssigned,
    onCancel: handleCloseMapAssignmentModal,
  });

  const flatMaps = useMemo(() => mapGroups.flatMap(group => group.maps), [mapGroups]);

  const visibleMapGroups = useMemo(
    () => mapGroups.filter(group => group.groupId !== MAP_GROUP_NAME_OTHERS && group.maps.length > 0),
    [mapGroups]
  );

  return (
    <MapAssignmentModalContainer
      title={
        <Flex align="flex-start" justify="space-between" gap={kccThemeConstants.SPACING.SM}>
          <Typography.Text style={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
            {t('mapAssignmentModal.title', { robotName: robot.name.toUpperCase() })}
          </Typography.Text>

          <MapAssignmentCloseButton type="link" aria-label={t('common.close')} onClick={handleCancel}>
            <SvgIcon name="cancel" />
          </MapAssignmentCloseButton>
        </Flex>
      }
      open={isMapAssignmentModalOpen}
      onCancel={handleCancel}
      closable={false}
      centered
      destroyOnClose
      width={800}
      footer={[
        <Button key="map-assignment-cancel" onClick={handleCancel} style={{ minWidth: '100px' }}>
          {t('common.cancel')}
        </Button>,
        <Button
          key="map-assignment-continue"
          type="primary"
          style={{ minWidth: '100px' }}
          disabled={selectedCount === 0}
          loading={isAssigning}
          onClick={handleAssignAndContinue}
        >
          {t('mapAssignmentModal.assignAndContinue')}
        </Button>,
      ]}
    >
      <MapAssignmentSearchContainer vertical gap={kccThemeConstants.SPACING.BASE}>
        <MapAssignmentSearchInput
          placeholder={t('mapAssignmentModal.searchPlaceholder')}
          prefix={
            <Flex>
              <SvgIcon name="search" />
            </Flex>
          }
          value={searchTerm}
          onChange={(event): void => handleSearchChange(event.target.value)}
          allowClear
        />

        {selectedCount > 0 && (
          <MapAssignmentSelectionTag
            role="button"
            tabIndex={0}
            onClick={handleClearSelection}
            onKeyDown={(event): void => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleClearSelection();
              }
            }}
          >
            <SvgIcon name="cancel" />
            {t('mapAssignmentModal.selectedCount', { count: selectedCount })}
          </MapAssignmentSelectionTag>
        )}
      </MapAssignmentSearchContainer>

      <MapAssignmentListsWrapper vertical>
        <MapAssignmentListContainer>
          <MapAssignmentListRow $isHeader>
            <MapAssignmentRowLeft>
              <KccCheckbox
                checked={mapGroupsHeaderState.checked}
                indeterminate={mapGroupsHeaderState.indeterminate}
                onChange={handleToggleAllMapGroups}
              />
            </MapAssignmentRowLeft>

            <MapAssignmentRowDivider />

            <MapAssignmentRowRight>
              <MapAssignmentSectionTitleText>
                {t('mapAssignmentModal.mapGroupSectionTitle')}
              </MapAssignmentSectionTitleText>
            </MapAssignmentRowRight>
          </MapAssignmentListRow>

          <MapAssignmentListRows>
            {isMapGroupsLoading && (
              <Flex align="center" justify="center" style={{ height: '100%' }}>
                <Spin />
              </Flex>
            )}

            {!isMapGroupsLoading && visibleMapGroups.length === 0 && (
              <Flex align="center" justify="center" style={{ height: '100%' }}>
                <Empty description={t('mapAssignmentModal.noMapGroupsResults')} />
              </Flex>
            )}

            {!isMapGroupsLoading &&
              visibleMapGroups.map((group, index) => {
                const { checked, indeterminate } = getGroupCheckedState(group);
                const groupMapNames = group.maps.map(map => map.name).join(', ');

                return (
                  <MapAssignmentListRow
                    key={group.groupId}
                    $noTopBorder={index === 0}
                    $noBottomBorder={index === visibleMapGroups.length - 1}
                    onClick={(): void => handleToggleGroup(group)}
                  >
                    <MapAssignmentRowLeft onClick={(event): void => event.stopPropagation()}>
                      <KccCheckbox
                        checked={checked}
                        indeterminate={indeterminate}
                        onChange={(): void => handleToggleGroup(group)}
                      />
                    </MapAssignmentRowLeft>

                    <MapAssignmentGroupRowRight>
                      <MapAssignmentGroupColumn>
                        <Typography.Text
                          ellipsis={{ tooltip: group.groupName }}
                          style={{ color: kccThemeConstants.COLORS.TEXT }}
                        >
                          {group.groupName}
                        </Typography.Text>
                      </MapAssignmentGroupColumn>

                      <MapAssignmentGroupColumn>
                        <Typography.Text
                          ellipsis={{ tooltip: groupMapNames }}
                          style={{ color: kccThemeConstants.COLORS.TEXT }}
                        >
                          {groupMapNames}
                        </Typography.Text>
                      </MapAssignmentGroupColumn>
                    </MapAssignmentGroupRowRight>
                  </MapAssignmentListRow>
                );
              })}
          </MapAssignmentListRows>
        </MapAssignmentListContainer>

        <MapAssignmentListContainer>
          <MapAssignmentListRow $isHeader>
            <MapAssignmentRowLeft>
              <KccCheckbox
                checked={mapsHeaderState.checked}
                indeterminate={mapsHeaderState.indeterminate}
                onChange={handleToggleAllMaps}
              />
            </MapAssignmentRowLeft>

            <MapAssignmentRowDivider />

            <MapAssignmentRowRight>
              <MapAssignmentSectionTitleText>{t('mapAssignmentModal.mapSectionTitle')}</MapAssignmentSectionTitleText>
            </MapAssignmentRowRight>
          </MapAssignmentListRow>

          <MapAssignmentListRows>
            {isMapsLoading && (
              <Flex align="center" justify="center" style={{ height: '100%' }}>
                <Spin />
              </Flex>
            )}

            {!isMapsLoading && flatMaps.length === 0 && (
              <Flex align="center" justify="center" style={{ height: '100%' }}>
                <Empty description={t('mapAssignmentModal.noResults')} />
              </Flex>
            )}

            {!isMapsLoading &&
              flatMaps.map((map, index) => (
                <MapAssignmentListRow
                  key={map.id}
                  $noTopBorder={index === 0}
                  $noBottomBorder={index === flatMaps.length - 1}
                  onClick={(): void => handleToggleMap(map.id)}
                >
                  <MapAssignmentRowLeft onClick={(event): void => event.stopPropagation()}>
                    <KccCheckbox checked={selectedMapIds.has(map.id)} onChange={(): void => handleToggleMap(map.id)} />
                  </MapAssignmentRowLeft>

                  <MapAssignmentMapItemRight gap={kccThemeConstants.SPACING.XS}>
                    <MapAssignmentMapImage
                      width={kccThemeConstants.SIZE.S400}
                      height={kccThemeConstants.SIZE.S400}
                      src={map.thumbnail ?? map.preview ?? mapPlaceHolder}
                      fallback={mapPlaceHolder}
                      alt={map.name}
                      preview={false}
                    />
                    <Typography.Text ellipsis={{ tooltip: map.name }} style={{ minWidth: 0, flex: 1 }}>
                      {map.name}
                    </Typography.Text>
                  </MapAssignmentMapItemRight>
                </MapAssignmentListRow>
              ))}
          </MapAssignmentListRows>
        </MapAssignmentListContainer>
      </MapAssignmentListsWrapper>
    </MapAssignmentModalContainer>
  );
};
